/**
 * AchievementCell (Plan 02-14, Task 1; Merker-skissen fra #156, steg 6c).
 *
 * Et glasskort med merkeskiven (`AchievementBadge` 52 px, terskelen som tall),
 * navnet og en linje under. Skiven har de tre tilstandene fra skissen:
 *   1. unlocked              fylt skive med lime glorie; «Låst opp» i aksent;
 *                            tap → onClick() (opens AchievementSheet)
 *   2. locked-non-secret     stiplet skive; navnet og målet («25 oppgaver»,
 *                            fra `goal` i registeret) står, så barnet ser
 *                            hva det kan få;
 *                            tap → toast "Ennå låst — fortsett å gjøre oppgaver"
 *   3. locked-secret         flat skive med lås; «Hemmelig» og «???» i stedet
 *                            for navnet; tap → toast "Hemmelig prestasjon — du finner den"
 *
 * Fremdrift per merke («18 av 25») har ingen data ennå (MAPPING.md, Merker),
 * så linjen under viser målet og ikke hvor langt barnet har kommet.
 *
 * The third state is the safety surface for T-02-27 (cross-member secret
 * leak). The server already blanks `title_key` for cross-member viewers;
 * even if the title_key is present (own/admin viewer), the locked-secret
 * variant still hides the title to preserve the surprise on first unlock.
 *
 * Tilstanden står i teksten under navnet, ikke bare i skiven, så fargen er
 * aldri eneste kjennetegn.
 */
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import { achievementGoal } from '@stell/shared';
import { AchievementBadge } from '../../components/primitives/AchievementBadge';
import { GLASS_SURFACE } from '../../components/primitives/Card';
import { toast } from '../../lib/toast';

export interface AchievementCellEntry {
  key: string;
  /** Server may blank for cross-member secret-locked entries. */
  title_key: string | null;
  /** Server may blank for cross-member secret-locked entries. */
  icon_key: string | null;
  secret: boolean;
  unlocked: boolean;
  unlocked_at: string | null;
}

interface AchievementCellProps {
  achievement: AchievementCellEntry;
  /** Tap handler — only fires for the unlocked state; locked taps fire toasts. */
  onClick: () => void;
  /** Merket push-lenken peker på (`?focus=`), med en ring rundt kortet. */
  focused?: boolean;
}

export function AchievementCell({ achievement, onClick, focused = false }: AchievementCellProps): ReactNode {
  const { t } = useTranslation();
  const { key, title_key, secret, unlocked } = achievement;

  const isLockedSecret = !unlocked && secret;
  const isLockedNonSecret = !unlocked && !secret;

  // Resolved title: in locked-non-secret with a server-blanked title_key
  // (cross-member edge case) we fall back to empty — layout stays stable.
  const title = title_key ? t(title_key) : '';
  const name = isLockedSecret ? t('achievements.cell.secretName') : title;
  // Målet bygges av terskelen i registeret (`goal`), så tallet aldri står
  // et annet sted enn der evaluatoren leser det. Ukens regent har ingen
  // terskel og en egen tekst.
  const goal = achievementGoal(key);
  const sub = unlocked
    ? t('achievements.cell.unlocked')
    : isLockedSecret
      ? t('achievements.cell.secretSub')
      : goal
        ? t(`achievements.goal.${goal.kind}`, { count: goal.count })
        : t(`achievement.${key}.goal`, { defaultValue: '' });

  function handleClick(): void {
    if (unlocked) {
      onClick();
      return;
    }
    if (isLockedNonSecret) {
      toast({ kind: 'info', body: t('achievements.locked.toast.nonSecret') });
      return;
    }
    if (isLockedSecret) {
      toast({ kind: 'info', body: t('achievements.locked.toast.secret') });
      return;
    }
  }

  // «???» leses ikke opp; et hemmelig merke får toast-teksten som navn. Et
  // låst merke sier at det er låst, ikke bare målet.
  const ariaLabel = isLockedSecret
    ? t('achievements.locked.toast.secret')
    : unlocked
      ? [title, sub].filter(Boolean).join(', ')
      : [title, t('achievements.cell.locked'), sub].filter(Boolean).join(', ');

  return (
    <button
      type="button"
      data-testid={`achievement-cell-${key}`}
      data-state={
        unlocked
          ? 'unlocked'
          : isLockedSecret
            ? 'locked-secret'
            : 'locked-non-secret'
      }
      data-focused={focused ? 'true' : undefined}
      onClick={handleClick}
      aria-label={t('achievements.cell_aria', { label: ariaLabel })}
      className={[
        GLASS_SURFACE,
        'flex h-full min-h-28 w-full flex-col items-center gap-1.5 rounded-row px-1.5 pt-3 pb-2.5 text-center text-ink shadow-md',
        focused ? 'outline-2 outline-offset-2 outline-accent-text' : '',
      ].join(' ')}
    >
      <AchievementBadge keyName={key} unlocked={unlocked} secret={secret} size={52} art="glyph" />
      {name ? (
        <span className="text-caption leading-tight font-extrabold">{name}</span>
      ) : null}
      {sub ? (
        <span className={`text-label font-semibold ${unlocked ? 'text-accent-text' : 'text-ink-2'}`}>
          {sub}
        </span>
      ) : null}
    </button>
  );
}
